import React, { useState } from 'react';
import { Search, Filter, Clock, User, MessageSquare, ChevronDown, Loader2, AlertCircle, RefreshCw } from 'lucide-react';
import { useAdminSessions } from '../../hooks/useAdminSessions';
import { AdminSession } from '../../types/admin';
import { formatRelativeTime } from '../../utils/time';

// Helper function to safely extract preview text from lastMessagePreview
const getPreviewText = (preview: string | { preview: string; timestamp: string } | undefined): string => {
  if (!preview) return '';
  if (typeof preview === 'string') return preview;
  if (typeof preview === 'object' && preview.preview) return preview.preview;
  return JSON.stringify(preview);
};

interface SessionsListProps {
  onSessionSelect?: (session: AdminSession) => void;
  selectedSessionId?: string;
  className?: string;
}

interface FilterState {
  userIdFilter: string;
  titleFilter: string;
  sessionType: 'all' | 'chat' | 'foundry';
  showFilters: boolean;
}

export const SessionsList: React.FC<SessionsListProps> = ({
  onSessionSelect,
  selectedSessionId,
  className = ''
}) => {
  const [filterState, setFilterState] = useState<FilterState>({
    userIdFilter: '',
    titleFilter: '',
    sessionType: 'all',
    showFilters: false
  });

  const {
    sessions,
    loading,
    error,
    hasMore,
    totalCount,
    loadMore,
    setFilter,
    refresh,
    clearError
  } = useAdminSessions({
    initialLimit: 50,
    autoLoad: true
  });

  // Apply filters when filter state changes - now uses LOCAL filtering only
  React.useEffect(() => {
    const adminFilter = {
      userIdFilter: filterState.userIdFilter.trim() || '',
      titleFilter: filterState.titleFilter.trim() || '',
      sessionType: filterState.sessionType === 'all' ? undefined : filterState.sessionType as 'chat' | 'foundry',
      limit: 50 // Keep limit for pagination purposes
    };
    
    // This now only updates local filter state, no backend calls
    setFilter(adminFilter);
  }, [filterState.userIdFilter, filterState.titleFilter, filterState.sessionType, setFilter]);

  // displaySessions are now already filtered by the hook - no need to sort again
  const displaySessions = sessions;

  const handleFilterChange = (field: keyof FilterState, value: string | boolean) => {
    setFilterState(prev => ({
      ...prev,
      [field]: value
    }));
  };

  const handleSessionClick = (session: AdminSession) => {
    onSessionSelect?.(session);
  };

  const handleLoadMore = async () => {
    if (!loading && hasMore) {
      await loadMore();
    }
  };

  const handleRefresh = async () => {
    clearError();
    await refresh();
  };

  return (
    <div className={`bg-white h-full flex flex-col ${className}`}>
      {/* Header */}
      <div className="border-b border-gray-200 p-4">
        <div className="flex items-center justify-between mb-3">
          <h2 className="text-lg font-semibold text-gray-900 flex items-center">
            <MessageSquare className="w-5 h-5 mr-2 text-purple-600" />
            All Sessions
          </h2>
          <div className="flex items-center space-x-2">
            <button
              onClick={handleRefresh}
              disabled={loading}
              className="p-1.5 text-gray-400 hover:text-gray-600 disabled:opacity-50"
              title="Refresh sessions"
            >
              <RefreshCw className={`w-4 h-4 ${loading ? 'animate-spin' : ''}`} />
            </button>
            <button
              onClick={() => handleFilterChange('showFilters', !filterState.showFilters)}
              className="p-1.5 text-gray-400 hover:text-gray-600"
              title="Toggle filters"
            >
              <Filter className="w-4 h-4" />
            </button>
          </div>
        </div>

        {/* Search */}
        <div className="relative">
          <Search className="w-4 h-4 absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400" />
          <input
            type="text"
            placeholder="Search by title or user ID..."
            value={filterState.titleFilter}
            onChange={(e) => handleFilterChange('titleFilter', e.target.value)}
            className="w-full pl-10 pr-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-purple-500 focus:border-purple-500"
          />
        </div>

        {/* Filters */}
        {filterState.showFilters && (
          <div className="mt-3 space-y-3 p-3 bg-gray-50 rounded-lg">
            <div>
              <label className="block text-xs font-medium text-gray-700 mb-1">
                User ID Filter
              </label>
              <div className="relative">
                <User className="w-4 h-4 absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400" />
                <input
                  type="text"
                  placeholder="Filter by user ID substring..."
                  value={filterState.userIdFilter}
                  onChange={(e) => handleFilterChange('userIdFilter', e.target.value)}
                  className="w-full pl-10 pr-3 py-2 border border-gray-300 rounded text-sm focus:ring-2 focus:ring-purple-500 focus:border-purple-500"
                />
              </div>
            </div>
            
            <div>
              <label className="block text-xs font-medium text-gray-700 mb-1">
                Session Type
              </label>
              <select
                value={filterState.sessionType}
                onChange={(e) => handleFilterChange('sessionType', e.target.value)}
                className="w-full px-3 py-2 border border-gray-300 rounded text-sm focus:ring-2 focus:ring-purple-500 focus:border-purple-500"
              >
                <option value="all">All Types</option>
                <option value="chat">Chat</option>
                <option value="foundry">Foundry</option>
              </select>
            </div>
          </div>
        )}

        {/* Results count */}
        {totalCount > 0 && (
          <div className="mt-2 text-xs text-gray-500">
            Showing {totalCount} session{totalCount !== 1 ? 's' : ''}
            {hasMore && ' (more available)'}
          </div>
        )}
      </div>

      {/* Error State */}
      {error && (
        <div className="p-4 bg-red-50 border-b border-red-200">
          <div className="flex items-start">
            <AlertCircle className="w-4 h-4 text-red-500 mt-0.5 mr-2" />
            <div>
              <p className="text-sm text-red-700">{error}</p>
              <button
                onClick={handleRefresh}
                className="mt-1 text-xs text-red-600 hover:text-red-800 underline"
              >
                Try again
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Sessions List */}
      <div className="flex-1 overflow-y-auto">
        {displaySessions.length === 0 ? (
          <div className="p-4 text-center text-gray-500">
            {loading ? (
              <div className="flex items-center justify-center py-8">
                <Loader2 className="w-6 h-6 animate-spin mr-2" />
                Loading sessions...
              </div>
            ) : (
              <div className="py-8">
                <MessageSquare className="w-12 h-12 mx-auto mb-3 text-gray-300" />
                <p className="text-sm">No sessions found</p>
                {(filterState.userIdFilter || filterState.titleFilter || filterState.sessionType !== 'all') && (
                  <p className="text-xs mt-1">Try adjusting your filters</p>
                )}
              </div>
            )}
          </div>
        ) : (
          <div className="divide-y divide-gray-200">
            {displaySessions.map((session) => (
              <div
                key={session.id}
                onClick={() => handleSessionClick(session)}
                className={`p-4 hover:bg-gray-50 cursor-pointer transition-colors ${
                  selectedSessionId === session.id ? 'bg-purple-50 border-r-2 border-purple-500' : ''
                }`}
              >
                <div className="flex items-start justify-between">
                  <div className="flex-1 min-w-0">
                    {/* Title */}
                    <h3 className="text-sm font-medium text-gray-900 truncate" title={session.title}>
                      {session.title}
                    </h3>
                    
                    {/* User ID */}
                    <div className="flex items-center mt-1">
                      <User className="w-3 h-3 text-gray-400 mr-1" />
                      <span className="text-xs text-gray-600 truncate" title={session.userId}>
                        {session.userId}
                      </span>
                    </div>
                    
                    {/* Session info */}
                    <div className="flex items-center mt-2 space-x-3">
                      <span className={`inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium ${
                        session.sessionType === 'foundry' 
                          ? 'bg-blue-100 text-blue-800' 
                          : 'bg-green-100 text-green-800'
                      }`}>
                        {session.sessionType}
                      </span>
                      
                      <span className="text-xs text-gray-500 flex items-center">
                        <MessageSquare className="w-3 h-3 mr-1" />
                        {session.messageCount}
                      </span>
                      
                      {session.isProcessing && (
                        <span className="text-xs text-orange-600 flex items-center">
                          <Loader2 className="w-3 h-3 mr-1 animate-spin" />
                          Processing
                        </span>
                      )}
                      
                      {session.isError && (
                        <span className="text-xs text-red-600 flex items-center">
                          <AlertCircle className="w-3 h-3 mr-1" />
                          Error
                        </span>
                      )}
                    </div>
                  </div>
                  
                  {/* Time */}
                  <div className="flex-shrink-0 ml-3">
                    <div className="flex items-center text-xs text-gray-500">
                      <Clock className="w-3 h-3 mr-1" />
                      {formatRelativeTime(session.updatedAt)}
                    </div>
                  </div>
                </div>
                
                {/* Last message preview */}
                {session.lastMessagePreview && (
                  <p className="text-xs text-gray-500 mt-2 truncate" title={getPreviewText(session.lastMessagePreview)}>
                    "{getPreviewText(session.lastMessagePreview)}"
                  </p>
                )}
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Load More Button */}
      {hasMore && displaySessions.length > 0 && (
        <div className="border-t border-gray-200 p-4">
          <button
            onClick={handleLoadMore}
            disabled={loading}
            className="w-full flex items-center justify-center px-4 py-2 border border-gray-300 rounded-lg text-sm font-medium text-gray-700 bg-white hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
          >
            {loading ? (
              <>
                <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                Loading more...
              </>
            ) : (
              <>
                <ChevronDown className="w-4 h-4 mr-2" />
                Load More Sessions (50 more)
              </>
            )}
          </button>
        </div>
      )}
    </div>
  );
};